import React, { useState } from 'react';
import { BarChartIcon, CreditCardIcon, LogOutIcon, MenuIcon, XIcon, LayoutGridIcon, TerminalIcon, FileTextIcon, CalendarIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, ShoppingCartIcon, SettingsIcon, GlobeIcon, GraduationCapIcon, ChevronDownIcon, SmartphoneIcon, BanknoteIcon } from 'lucide-react';
interface AppLayoutProps {
  children: React.ReactNode;
}
export function AppLayout({
  children
}: AppLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [bookingsExpanded, setBookingsExpanded] = useState(false);
  const [terminalsExpanded, setTerminalsExpanded] = useState(false);
  const [transactionsExpanded, setTransactionsExpanded] = useState(false);
  const navigate = (path: string) => {
    window.location.href = path;
  };
  const isActive = (path: string) => {
    if (path === '/overview') {
      return window.location.pathname === '/overview' || window.location.pathname === '/';
    }
    return window.location.pathname === path;
  };
  const isPaymentRequestsActive = () => {
    return window.location.pathname === '/payment-requests';
  };
  // Get the current page title based on the URL
  const getPageTitle = () => {
    if (window.location.pathname === '/overview' || window.location.pathname === '/') return 'Overview';
    if (window.location.pathname === '/payment-requests') return 'Payment Requests';
    if (window.location.pathname === '/settings') return 'Settings';
    if (window.location.pathname === '/virtual-terminal') return 'Virtual Terminal';
    if (window.location.pathname === '/physical-terminals') return 'Physical Terminals';
    if (window.location.pathname === '/softpos-terminals') return 'SoftPos Terminals';
    if (window.location.pathname === '/connect') return 'Connect OAuth2 Apps';
    if (window.location.pathname === '/orders') return 'Orders';
    if (window.location.pathname === '/tutorials') return 'Tutorials';
    return 'Dashboard';
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-38">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 right-0 p-4 z-20" data-id="element-39">
        <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded-md bg-white shadow-sm border border-gray-200" data-id="element-40">
          {menuOpen ? <XIcon size={20} data-id="element-41" /> : <MenuIcon size={20} data-id="element-42" />}
        </button>
      </div>
      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 w-64 bg-[#1a2042] z-10 transform transition-transform duration-200 ease-in-out
        ${menuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} data-id="element-43">
        <div className="h-full flex flex-col" data-id="element-44">
          {/* Logo */}
          <div className="p-4 border-b border-[#2a3154] flex items-center" data-id="element-45">
            <div className="flex items-center" data-id="element-46">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/w9icsAoc3LreXhemq2jM7K/Logo_of_Bank_of_Maldives.svg" alt="Bank of Maldives" className="w-8 h-8 rounded-sm" data-id="element-47" />
              <span className="ml-2 text-white font-semibold" data-id="element-48">
                Bank of Maldives
              </span>
            </div>
          </div>
          {/* Navigation */}
          <nav className="flex-1 py-4 space-y-1 text-gray-300" data-id="element-49">
            <button onClick={() => navigate('/overview')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/overview') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-50">
              <BarChartIcon size={18} className="mr-3" data-id="element-51" />
              Overview
            </button>
            <button onClick={() => navigate('/payment-requests')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/payment-requests') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-52">
              <LayoutGridIcon size={18} className="mr-3" data-id="element-53" />
              Payment requests
            </button>
            {/* Terminals Collapsible Menu */}
            <div data-id="element-54">
              <button onClick={() => setTerminalsExpanded(!terminalsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals') || isActive('/connect') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-55">
                <div className="flex items-center" data-id="element-56">
                  <TerminalIcon size={18} className="mr-3" data-id="element-57" />
                  Terminals
                </div>
                <ChevronDownIcon size={16} className={`transition-transform ${terminalsExpanded ? 'rotate-180' : ''}`} data-id="element-58" />
              </button>
              {/* Terminal submenu items */}
              {terminalsExpanded && <div className="ml-7 pl-4 border-l border-[#2a3154]" data-id="element-59">
                  <button onClick={() => navigate('/virtual-terminal')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/virtual-terminal') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-60">
                    <BanknoteIcon size={16} className="mr-3" data-id="element-61" />
                    Virtual
                  </button>
                  <button onClick={() => navigate('/physical-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/physical-terminals') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-62">
                    <CreditCardIcon size={16} className="mr-3" data-id="element-63" />
                    Physical
                  </button>
                  <button onClick={() => navigate('/softpos-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/softpos-terminals') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-64">
                    <SmartphoneIcon size={16} className="mr-3" data-id="element-65" />
                    SoftPos
                  </button>
                  <button onClick={() => navigate('/connect')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/connect') ? 'text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-66">
                    <GlobeIcon size={16} className="mr-3" data-id="element-67" />
                    Connect
                  </button>
                </div>}
            </div>
            <button onClick={() => navigate('/invoices')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/invoices') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-68">
              <FileTextIcon size={18} className="mr-3" data-id="element-69" />
              Invoices
            </button>
            <div className="border-t border-[#2a3154]" data-id="element-70"></div>
            <button onClick={() => setBookingsExpanded(!bookingsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isActive('/bookings') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-71">
              <div className="flex items-center" data-id="element-72">
                <CalendarIcon size={18} className="mr-3" data-id="element-73" />
                Bookings
              </div>
              <ChevronDownIcon size={16} className={`transition-transform ${bookingsExpanded ? 'rotate-180' : ''}`} data-id="element-74" />
            </button>
            <button onClick={() => navigate('/customers')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/customers') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-75">
              <UsersIcon size={18} className="mr-3" data-id="element-76" />
              Customers
            </button>
            <button onClick={() => setTransactionsExpanded(!transactionsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isActive('/transactions') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-77">
              <div className="flex items-center" data-id="element-78">
                <ArrowLeftRightIcon size={18} className="mr-3" data-id="element-79" />
                Transactions
              </div>
              <ChevronDownIcon size={16} className={`transition-transform ${transactionsExpanded ? 'rotate-180' : ''}`} data-id="element-80" />
            </button>
            <button onClick={() => navigate('/shops')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/shops') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-81">
              <ShoppingBagIcon size={18} className="mr-3" data-id="element-82" />
              Shops
            </button>
            <button onClick={() => navigate('/orders')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/orders') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-83">
              <ShoppingCartIcon size={18} className="mr-3" data-id="element-84" />
              Orders
            </button>
            <div className="border-t border-[#2a3154]" data-id="element-85"></div>
            <button onClick={() => navigate('/settings')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/settings') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-86">
              <SettingsIcon size={18} className="mr-3" data-id="element-87" />
              Settings
            </button>
            <button onClick={() => navigate('/tutorials')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/tutorials') ? 'bg-[#2a3154] text-white' : 'hover:bg-[#2a3154]'}`} data-id="element-88">
              <GraduationCapIcon size={18} className="mr-3" data-id="element-89" />
              Tutorials
            </button>
          </nav>
        </div>
      </div>
      {/* Main content */}
      <div className="flex-1 lg:pl-64" data-id="element-90">
        <div className="bg-white border-b border-gray-200" data-id="element-91">
          <div className="flex justify-between items-center px-6 py-4" data-id="element-92">
            <h1 className="text-xl font-semibold text-gray-900" data-id="element-93">
              {getPageTitle()}
            </h1>
            <div className="flex items-center" data-id="element-94">
              <div className="w-10 h-10 bg-[#f8d7d7] rounded-full flex items-center justify-center text-[#e54c3c] font-medium" data-id="element-95">
                SV
              </div>
              <div className="ml-3 hidden sm:block" data-id="element-96">
                <p className="text-sm font-medium text-gray-800" data-id="element-97">
                  Simon Verraest
                </p>
                <p className="text-xs text-gray-500" data-id="element-98">BML EXPERIENCE ZONE</p>
              </div>
              <ChevronDownIcon size={16} className="ml-2 text-gray-500" data-id="element-99" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-100">
          {children}
        </div>
      </div>
      {/* Overlay for mobile */}
      {menuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-0 lg:hidden" onClick={() => setMenuOpen(false)} data-id="element-101"></div>}
    </div>;
}